/** High-score tables (docs/spec/04 §6): ten lines each, initials entered arcade style. Pure. */

export type TableId = 'supernova' | 'classic';
export const TABLE_IDS: readonly TableId[] = ['supernova', 'classic'];
export const TABLE_SIZE = 10;

export interface ScoreEntry {
  /** Three characters. */
  readonly initials: string;
  readonly score: number;
  /** The stage the game ended on. */
  readonly stage: number;
  /** YYYY-MM-DD. */
  readonly date: string;
}

export type Tables = Readonly<Record<TableId, readonly ScoreEntry[]>>;

const DEFAULT_NAMES = ['HIV', 'BEE', 'MOT', 'ACE', 'ZAP', 'NVA', 'SUN', 'ORB', 'RAY', 'DOT'];

/** The tables a new player sees: modest scores to beat, 20,000 at the top as the cabinet had. */
export function defaultTables(): Tables {
  const make = (top: number): ScoreEntry[] =>
    DEFAULT_NAMES.map((initials, i) => ({
      initials,
      score: Math.round((top * (TABLE_SIZE - i)) / TABLE_SIZE / 10) * 10,
      stage: Math.max(1, 6 - Math.floor(i / 2)),
      date: '1981-09-01',
    }));
  return { supernova: make(30000), classic: make(20000) };
}

/** Where `score` would land (0-based), or -1 if it does not make the table. */
export function rankFor(table: readonly ScoreEntry[], score: number): number {
  if (score <= 0) return -1;
  const i = table.findIndex((e) => score > e.score);
  if (i >= 0) return i;
  return table.length < TABLE_SIZE ? table.length : -1;
}

export function cleanInitials(s: string): string {
  return (s.toUpperCase().replace(/[^A-Z0-9 .!-]/g, '') + '   ').slice(0, 3);
}

export function insertScore(tables: Tables, id: TableId, entry: ScoreEntry): { tables: Tables; rank: number } {
  const table = tables[id];
  const rank = rankFor(table, entry.score);
  if (rank < 0) return { tables, rank };
  const clean = { ...entry, initials: cleanInitials(entry.initials) };
  const next = [...table.slice(0, rank), clean, ...table.slice(rank)].slice(0, TABLE_SIZE);
  return { tables: { ...tables, [id]: next }, rank };
}

/** The valid entries of an untrusted list, best first, at most a table's worth. */
export function parseEntries(list: readonly unknown[]): ScoreEntry[] {
  return list
    .filter((e): e is ScoreEntry => typeof e === 'object' && e !== null && typeof (e as ScoreEntry).score === 'number')
    .map((e) => ({
      initials: cleanInitials(String(e.initials)),
      score: Math.max(0, Math.floor(e.score)),
      stage: Math.max(1, Math.floor(Number(e.stage) || 1)),
      date: String(e.date ?? '').slice(0, 10),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, TABLE_SIZE);
}

/** Parse stored tables, keeping whatever is valid and filling the rest with defaults. */
export function parseTables(json: string | null): Tables {
  const fallback = defaultTables();
  if (!json) return fallback;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const out: Record<TableId, readonly ScoreEntry[]> = { ...fallback };
    for (const id of TABLE_IDS) {
      const list = raw[id];
      if (Array.isArray(list)) out[id] = parseEntries(list);
    }
    return out;
  } catch {
    return fallback;
  }
}
